import type { CursorPosition } from './lib/save-cursor';

import { useStore } from '@tanstack/react-form';
import { useCallback, useEffect, useRef } from 'react';

import { getElementBySelector } from '../../../lib/helpers';
import { restoreCursor } from './lib/restore-cursor';
import { getCursor } from './lib/save-cursor';

import { useFormContext } from '../../form';

const FocusAfterSaveHandler = ({ formId }: { formId: string }) => {
  const form = useFormContext();
  const lastFocusedPositionRef = useRef<CursorPosition | undefined>(null);
  const isSubmittingRef = useRef(false);
  const focusInRef = useRef<boolean>(null);
  const isEditPageRef = useRef(window.location.pathname.includes('/edit/'));

  const { isSubmitting } = useStore(form.store, (state) => ({
    isSubmitting: state.isSubmitting,
  }));

  const handleFocusOut = useCallback((event: FocusEvent) => {
    lastFocusedPositionRef.current = getCursor(event);
    focusInRef.current = false;
  }, []);

  const handleFocusIn = useCallback(() => {
    focusInRef.current = true;
  }, []);

  useEffect(() => {
    const formElement = document.getElementById(formId);
    if (!formElement) return;

    formElement.addEventListener('focusin', handleFocusIn);
    formElement.addEventListener('focusout', handleFocusOut);
    return () => {
      formElement.removeEventListener('focusin', handleFocusIn);
      formElement.removeEventListener('focusout', handleFocusOut);
    };
  }, [handleFocusOut, formId, handleFocusIn]);

  const restoreFocus = useCallback(() => {
    setTimeout(() => {
      if (focusInRef.current || !lastFocusedPositionRef.current) return;
      restoreCursor(lastFocusedPositionRef.current);
    }, 200);
  }, []);

  useEffect(() => {
    const wasSubmitting = isSubmittingRef.current;
    isSubmittingRef.current = isSubmitting;

    if (wasSubmitting && !isSubmitting) {
      if (isEditPageRef.current) {
        restoreFocus();
        return;
      }

      getElementBySelector(`#${formId}`, false, 100).then(() => {
        getElementBySelector(`#${formId}`).then(() => {
          restoreFocus();
          isEditPageRef.current = true;
        });
      });
    }
  }, [formId, isSubmitting, restoreFocus]);

  return null;
};

export default FocusAfterSaveHandler;
